import React, { useState, useEffect, useRef } from 'react';
import { webrtcVoiceService, VoiceCallSession } from '../services/webrtcService';
import { useClient } from '../contexts/ClientContext';
import { PhoneIcon, StarIcon, MicIcon, MicOffIcon, Volume2Icon, CheckCircleIcon } from './Icons';

export const LiveVoiceCallModal: React.FC = () => {
    const { deductVoiceCallPoints } = useClient();
    const [session, setSession] = useState<VoiceCallSession | null>(null);
    const [duration, setDuration] = useState<number>(0);
    const [isMuted, setIsMuted] = useState<boolean>(false);
    const [audioLevel, setAudioLevel] = useState<number>(0);
    const [rating, setRating] = useState<number>(5);
    const [feedback, setFeedback] = useState<string>('');
    const [pointsReport, setPointsReport] = useState<{ minutesUsed: number; pointsDeducted: number; remainingPoints: number } | null>(null);
    const [ratingSubmitted, setRatingSubmitted] = useState<boolean>(false);

    // Formulaire de rappel automatique (Callback)
    const [showCallbackForm, setShowCallbackForm] = useState<boolean>(false);
    const [callbackPhone, setCallbackPhone] = useState<string>('');
    const [callbackNotes, setCallbackNotes] = useState<string>('');
    const [callbackSuccess, setCallbackSuccess] = useState<boolean>(false);

    // Voicemail Recording
    const [isRecordingVoicemail, setIsRecordingVoicemail] = useState<boolean>(false);
    const [voicemailDuration, setVoicemailDuration] = useState<number>(0);
    const [voicemailSaved, setVoicemailSaved] = useState<boolean>(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const voicemailTimerRef = useRef<any>(null);

    const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
    const remoteAudioRef = useRef<HTMLAudioElement | null>(null);

    // Abonnement aux flux audio et aux sessions WebRTC
    useEffect(() => {
        const unsubStream = webrtcVoiceService.subscribeRemoteStream((stream) => {
            setRemoteStream(stream);
        });

        const unsubSession = webrtcVoiceService.subscribe((s) => {
            setSession(s);
            if (s) {
                setIsMuted(s.isMuted);
                setDuration(s.durationSeconds);
                if (s.status === 'ended' && s.durationSeconds > 0 && !pointsReport && s.role === 'caller') {
                    if (deductVoiceCallPoints) {
                        const report = deductVoiceCallPoints(s.durationSeconds, s.mediumName);
                        setPointsReport(report);
                    }
                }
            }
        });

        const unsubAudio = webrtcVoiceService.subscribeAudioLevel((level) => {
            setAudioLevel(level);
        });

        return () => {
            unsubStream();
            unsubSession();
            unsubAudio();
        };
    }, [pointsReport, deductVoiceCallPoints]);

    // Déclenchement automatique de l'enregistrement vocal lors du passage sur répondeur
    useEffect(() => {
        if (session && session.status === 'autoanswer' && !isRecordingVoicemail && !voicemailSaved) {
            startVoicemailRecording();
        }
        return () => {
            if (voicemailTimerRef.current) clearInterval(voicemailTimerRef.current);
        };
    }, [session?.status]);

    const startVoicemailRecording = async () => {
        try {
            audioChunksRef.current = [];
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };

            recorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                if (audioBlob.size > 0) {
                    try {
                        const callerName = encodeURIComponent(session?.clientName || 'Client');
                        await fetch(`/api/voicemail/upload?callerId=${callerName}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'audio/webm' },
                            body: audioBlob
                        });
                        setVoicemailSaved(true);
                    } catch (err) {
                        console.error('[Voicemail] Upload error:', err);
                    }
                }
                stream.getTracks().forEach(t => t.stop());
            };

            recorder.start(1000);
            setIsRecordingVoicemail(true);
            setVoicemailDuration(0);
            voicemailTimerRef.current = setInterval(() => {
                setVoicemailDuration(d => {
                    if (d >= 120) { // Max 2 minutes
                        stopVoicemailRecording();
                        return d;
                    }
                    return d + 1;
                });
            }, 1000);
        } catch (err) {
            console.error('[Voicemail] Mic access error:', err);
        }
    };

    const stopVoicemailRecording = () => {
        if (voicemailTimerRef.current) {
            clearInterval(voicemailTimerRef.current);
            voicemailTimerRef.current = null;
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        setIsRecordingVoicemail(false);
    };

    const handleFinishVoicemail = () => {
        stopVoicemailRecording();
        setTimeout(() => {
            handleHangup();
        }, 1500);
    };

    // Attachement et lecture automatique du flux audio distant
    useEffect(() => {
        if (remoteAudioRef.current && remoteStream) {
            remoteAudioRef.current.srcObject = remoteStream;
            remoteAudioRef.current.muted = false;
            remoteAudioRef.current.volume = 1.0;
            remoteAudioRef.current.play().catch(e => console.warn('[LiveVoice] Play stream note:', e));
        }
    }, [remoteStream, session]);

    if (!session || session.status === 'idle') return null;

    const isConnected = session.status === 'connected';
    const isCalling = session.status === 'calling';
    const isIncoming = session.status === 'incoming';
    const isQueued = session.status === 'queued';
    const isEnded = session.status === 'ended' || session.status === 'rejected' || session.status === 'busy';
    const isAutoAnswer = session.status === 'autoanswer';

    const formatTime = (secs: number) => {
        const m = Math.floor(secs / 60).toString().padStart(2, '0');
        const s = (secs % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const handleAccept = () => {
        webrtcVoiceService.acceptCall();
    };

    const handleReject = () => {
        webrtcVoiceService.rejectCall();
    };

    const handleHangup = () => {
        webrtcVoiceService.endCall();
    };

    const handleToggleMute = () => {
        const muted = webrtcVoiceService.toggleMute();
        setIsMuted(muted);
    };

    const handleCloseEnded = () => {
        webrtcVoiceService.resetCall();
        setPointsReport(null);
        setRatingSubmitted(false);
        setShowCallbackForm(false);
        setCallbackSuccess(false);
    };

    const handleUnlockAudio = () => {
        webrtcVoiceService.unlockAudioPlayback();
    };

    const handleSubmitCallback = (e: React.FormEvent) => {
        e.preventDefault();
        if (!callbackPhone) return;
        webrtcVoiceService.requestCallback(callbackPhone, callbackNotes);
        setCallbackSuccess(true);
        setTimeout(() => {
            handleHangup();
        }, 2000);
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-xl animate-fade-in select-none">
            {/* Lecteur Audio invisible pour le rendu sonore haute fidélité */}
            <audio ref={remoteAudioRef} autoPlay playsInline style={{ position: 'fixed', bottom: 0, right: 0, width: 1, height: 1, opacity: 0.01, pointerEvents: 'none' }} />

            <div className="absolute w-72 sm:w-96 h-72 sm:h-96 rounded-full bg-purple-600/25 blur-3xl pointer-events-none animate-pulse"></div>

            <div className="relative w-full max-w-sm sm:max-w-md bg-gradient-to-b from-[#15092a] via-[#0e051c] to-black border border-purple-500/40 rounded-3xl p-6 sm:p-8 shadow-[0_0_70px_rgba(139,92,246,0.35)] text-center text-white overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-gold-400 to-transparent"></div>

                {/* Bannière Déblocage Autoplay si requis */}
                {session.autoplayBlocked && isConnected && (
                    <div className="mb-4 p-3 bg-amber-500/20 border border-amber-500/50 rounded-2xl animate-bounce">
                        <p className="text-xs text-amber-200 font-bold mb-2">Le son est en attente d'autorisation</p>
                        <button
                            type="button"
                            onClick={handleUnlockAudio}
                            className="px-4 py-1.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold text-xs rounded-xl shadow cursor-pointer hover:scale-105 transition-all"
                        >
                            Activer l'Audio Maintenant
                        </button>
                    </div>
                )}

                {/* BANNIÈRE FILE D'ATTENTE INTELLIGENTE (ACD QUEUE) */}
                {isQueued && (
                    <div className="mb-5 p-4 bg-gradient-to-r from-indigo-950/90 via-purple-950/90 to-black border border-indigo-500/50 rounded-2xl shadow-xl text-center relative animate-fade-in">
                        {/* Audio d'attente en streaming */}
                        <audio 
                            autoPlay 
                            loop 
                            src={`/api/calls/music-on-hold?t=${Date.now()}`} 
                            style={{ position: 'absolute', opacity: 0.01, pointerEvents: 'none', width: 1, height: 1 }} 
                        />

                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/40 text-indigo-300 text-[11px] font-bold uppercase tracking-wider mb-2">
                            <span className="w-2 h-2 rounded-full bg-indigo-400 animate-ping"></span>
                            File d'attente prioritaire
                        </div>

                        <div className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-yellow-200 to-amber-400 my-1">
                            Position #{session.queuePosition || 1}
                        </div>

                        <p className="text-xs text-indigo-200 font-medium mb-3">
                            Temps d'attente estimé : <strong className="text-gold-400 font-mono">~{session.estimatedWaitMinutes || 2} min</strong>
                        </p>

                        <div className="w-full bg-black/40 border border-indigo-500/30 rounded-full h-2 overflow-hidden mb-3">
                            <div className="bg-gradient-to-r from-purple-500 via-indigo-500 to-gold-400 h-full w-2/3 animate-pulse rounded-full"></div>
                        </div>

                        <p className="text-[11px] text-gray-300 mb-3 leading-relaxed">
                            Tous nos conseillers sont actuellement en consultation. Vous serez mis en relation dès qu'un expert se libère.
                        </p>

                        {!showCallbackForm && !callbackSuccess && (
                            <button
                                type="button"
                                onClick={() => setShowCallbackForm(true)}
                                className="w-full py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-xs rounded-xl shadow-lg transition-all cursor-pointer flex items-center justify-center gap-2"
                            >
                                <PhoneIcon className="w-4 h-4 inline" />
                                <span>Me faire rappeler sans attendre</span>
                            </button>
                        )}

                        {showCallbackForm && !callbackSuccess && (
                            <form onSubmit={handleSubmitCallback} className="mt-3 bg-black/60 border border-purple-500/40 rounded-xl p-3 text-left space-y-2 animate-fade-in">
                                <label className="block text-[10px] text-purple-300 font-bold uppercase">Votre numéro de téléphone :</label>
                                <input
                                    type="tel"
                                    required
                                    value={callbackPhone}
                                    onChange={(e) => setCallbackPhone(e.target.value)}
                                    placeholder="Ex: 06 12 34 56 78"
                                    className="w-full px-3 py-1.5 bg-black/80 border border-gray-700 focus:border-gold-400 rounded-lg text-white text-xs outline-none"
                                />
                                <div className="flex gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={() => setShowCallbackForm(false)}
                                        className="flex-1 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-[10px] font-bold rounded-lg"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 py-1 bg-gold-500 hover:bg-gold-400 text-black text-[10px] font-bold rounded-lg shadow"
                                    >
                                        Confirmer le rappel
                                    </button>
                                </div>
                            </form>
                        )}

                        {callbackSuccess && (
                            <div className="mt-2 p-2 bg-emerald-500/20 border border-emerald-500/50 rounded-xl text-emerald-300 text-xs font-bold animate-fade-in">
                                Demande de rappel confirmée !
                            </div>
                        )}
                    </div>
                )}

                {/* Statut d'Appel standard */}
                {!isQueued && (
                    <div className="mb-4">
                        <span className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-purple-950/90 border border-purple-500/40 text-gold-300 text-[11px] font-bold uppercase tracking-wider mb-2 shadow-sm">
                            <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400 animate-pulse' : isCalling || isIncoming ? 'bg-amber-400 animate-ping' : 'bg-rose-500'}`}></span>
                            {isCalling && "Connexion avec nos voyants en cours (Sonnerie)..."}
                            {isIncoming && "Appel Vocal Entrant..."}
                            {isConnected && "Communication Audio en Direct"}
                            {isEnded && "Appel Terminé"}
                        </span>
                    </div>
                )}

                {/* Avatar avec halo réactif au volume de voix */}
                <div className="relative inline-block my-2">
                    <div
                        className="absolute inset-0 rounded-full bg-gold-400/30 blur-xl transition-all duration-75 pointer-events-none"
                        style={{ transform: `scale(${1 + Math.min(audioLevel / 35, 0.65)})`, opacity: isConnected ? 0.9 : 0.2 }}
                    ></div>
                    <img
                        src={session.mediumImage || '/sarah.jpg'}
                        alt={session.mediumName}
                        className={`relative w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-4 transition-all duration-300 shadow-2xl mx-auto ${
                            isConnected ? 'border-emerald-500 shadow-[0_0_30px_rgba(16,185,129,0.5)]' : 'border-purple-500/70 shadow-[0_0_30px_rgba(168,85,247,0.4)]'
                        }`}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/sarah.jpg'; }}
                    />
                </div>

                <h3 className="text-xl sm:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-white to-gold-400 mt-2 mb-0.5 tracking-wide">
                    {session.mediumName}
                </h3>
                <p className="text-xs text-purple-300 font-medium mb-3">
                    {session.role === 'caller' ? `Client : ${session.clientName}` : `Consultation Privée AstroX`}
                </p>

                {/* Chronomètre & Onde Vocale (Quand connecté) */}
                {isConnected && (
                    <div className="my-4 p-3 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-md">
                        <div className="text-3xl font-black font-mono tracking-widest text-emerald-400 drop-shadow-md">
                            {formatTime(duration)}
                        </div>
                        <div className="flex items-center justify-center gap-1.5 h-6 mt-2">
                            {[...Array(12)].map((_, i) => {
                                const height = isMuted ? 4 : Math.max(4, Math.min(24, (audioLevel * ((i % 5) + 1)) / 4.5));
                                return (
                                    <div
                                        key={i}
                                        className="w-1 bg-gradient-to-t from-emerald-500 to-gold-400 rounded-full transition-all duration-75"
                                        style={{ height: `${height}px` }}
                                    ></div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Actions pour Appel Entrant (Récepteur) */}
                {isIncoming && (
                    <div className="flex items-center justify-center gap-6 mt-6">
                        <button
                            type="button"
                            onClick={handleReject}
                            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-500 active:scale-95 text-white flex items-center justify-center shadow-[0_0_25px_rgba(239,68,68,0.6)] transition-all cursor-pointer"
                            title="Refuser l'appel"
                        >
                            <PhoneIcon className="w-7 h-7 rotate-[135deg]" />
                        </button>
                        <button
                            type="button"
                            onClick={handleAccept}
                            className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-400 active:scale-95 text-white flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.7)] transition-all animate-bounce cursor-pointer"
                            title="Décrocher l'appel"
                        >
                            <PhoneIcon className="w-7 h-7" />
                        </button>
                    </div>
                )}

                
                {/* Mode Répondeur Automatique (Auto-Answer Voicemail) */}
                {isAutoAnswer && (
                    <div className="my-4 p-4 bg-purple-950/80 border border-purple-500/50 rounded-2xl animate-fade-in text-center space-y-3">
                        <div className="flex items-center justify-center gap-2 text-gold-300 font-bold text-sm">
                            <span className="w-3 h-3 rounded-full bg-red-500 animate-ping"></span>
                            <span>Répondeur Automatique AstroX</span>
                        </div>
                        <p className="text-xs text-gray-300">
                            Tous nos conseillers sont actuellement occupés. Laissez votre message vocal après le signal sonore (max 2 min) :
                        </p>
                        
                        {!voicemailSaved ? (
                            <>
                                <div className="p-3 bg-black/50 border border-purple-500/30 rounded-xl flex items-center justify-center gap-3">
                                    <MicIcon className="w-6 h-6 text-purple-300 animate-pulse mx-auto" />
                                    <span className="font-mono text-emerald-400 font-bold text-lg">
                                        {formatTime(voicemailDuration)} / 02:00
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleFinishVoicemail}
                                    className="w-full py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold text-xs rounded-xl shadow-lg transition-all cursor-pointer"
                                >
                                    Terminer et Enregistrer le Message
                                </button>
                            </>
                        ) : (
                            <div className="p-3 bg-emerald-500/20 border border-emerald-500/40 rounded-xl text-xs text-emerald-300 font-bold space-y-1">
                                <p>Votre message vocal a été enregistré avec succès !</p>
                                <p className="text-[11px] text-gray-300 font-normal">Nos voyants vous rappelleront dès que possible.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Actions Pendant l'Appel Actif ou en Attente */}
                {(isConnected || isCalling || isQueued) && (
                    <div className="flex items-center justify-center gap-4 mt-6">
                        {isConnected && (
                            <button
                                type="button"
                                onClick={handleToggleMute}
                                className={`p-4 rounded-full border transition-all cursor-pointer active:scale-95 ${
                                    isMuted 
                                        ? 'bg-amber-500/20 border-amber-500 text-amber-300 shadow-[0_0_20px_rgba(245,158,11,0.4)]' 
                                        : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                                }`}
                                title={isMuted ? "Réactiver le micro" : "Couper le micro"}
                            >
                                <span className="flex items-center justify-center">{isMuted ? <MicOffIcon className="w-5 h-5 text-rose-400" /> : <MicIcon className="w-5 h-5 text-white" />}</span>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleHangup}
                            className="px-6 py-3.5 rounded-full bg-red-600 hover:bg-red-500 active:scale-95 text-white font-bold text-sm flex items-center gap-2 shadow-[0_0_30px_rgba(239,68,68,0.6)] transition-all cursor-pointer"
                        >
                            <PhoneIcon className="w-5 h-5 rotate-[135deg]" />
                            <span>{isQueued ? "Quitter la file" : "Raccrocher"}</span>
                        </button>
                    </div>
                )}

                {/* Récapitulatif Fin d'Appel & Notation */}
                {isEnded && (
                    <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-2xl animate-fade-in text-center space-y-3">
                        <h4 className="text-base font-bold text-gold-300">Consultation Terminée</h4>

                        {pointsReport && (
                            <div className="p-3 bg-purple-950/80 border border-purple-500/40 rounded-xl text-xs space-y-1">
                                <div className="flex justify-between text-gray-300">
                                    <span>Durée facturée :</span>
                                    <strong className="text-white">{pointsReport.minutesUsed} min</strong>
                                </div>
                                <div className="flex justify-between text-gray-300">
                                    <span>Points déduits :</span>
                                    <strong className="text-gold-400">-{pointsReport.pointsDeducted} Astres</strong>
                                </div>
                                <div className="flex justify-between text-gray-300 border-t border-purple-500/30 pt-1">
                                    <span>Solde restant :</span>
                                    <strong className="text-emerald-400">{pointsReport.remainingPoints} Astres</strong>
                                </div>
                            </div>
                        )}

                        {!ratingSubmitted ? (
                            <div className="space-y-2 pt-2">
                                <p className="text-xs text-gray-300">Comment évaluez-vous cet échange ?</p>
                                <div className="flex justify-center gap-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setRating(star)}
                                            className="p-1 text-gold-400 hover:scale-125 transition-transform cursor-pointer"
                                        >
                                            <StarIcon className={`w-6 h-6 ${star <= rating ? 'fill-current' : 'text-gray-600'}`} />
                                        </button>
                                    ))}
                                </div>
                                <textarea
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    placeholder="Votre commentaire (optionnel)..."
                                    rows={2}
                                    className="w-full mt-2 px-3 py-2 bg-black/60 border border-purple-500/30 rounded-xl text-xs text-white placeholder-gray-500 focus:outline-none focus:border-gold-400"
                                />
                                <button
                                    type="button"
                                    onClick={() => setRatingSubmitted(true)}
                                    className="w-full py-2 bg-gradient-to-r from-gold-500 to-amber-500 text-black font-bold text-xs rounded-xl shadow-lg hover:scale-102 transition-all cursor-pointer"
                                >
                                    Envoyer mon avis
                                </button>
                            </div>
                        ) : (
                            <div className="p-3 bg-emerald-500/20 border border-emerald-500/40 rounded-xl text-xs text-emerald-300 font-bold">
                                Merci pour votre précieux retour !
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={handleCloseEnded}
                            className="mt-2 text-xs text-gray-400 hover:text-white underline cursor-pointer"
                        >
                            Fermer
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
